import { useEffect, useState } from "react";
import { BETA_SECRET, track } from "../seller/lib/api";

const mono = "'DM Mono',monospace";
const sans = "'Outfit','Inter',sans-serif";

const fmtDate = (iso) => new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" });

const STATUS_COLORS = {
  new:       { bg: "#fef4e8", fg: "#c47c1a", border: "#f1d6a8" },
  contacted: { bg: "#e4f7ef", fg: "#2d7a62", border: "#b8e8d4" },
  closed:    { bg: "#f2f8f5", fg: "#7aad98", border: "#d8ede6" },
};

const StatusBadge = ({ status }) => {
  const c = STATUS_COLORS[status] || STATUS_COLORS.new;
  return (
    <span style={{ fontFamily: mono, fontSize: 10, letterSpacing: "0.08em", textTransform: "uppercase", padding: "3px 10px", borderRadius: 10, background: c.bg, color: c.fg, border: `1px solid ${c.border}` }}>
      {status}
    </span>
  );
};

// Shown only when the real fetch genuinely FAILS (network/server error) —
// never substituted for a legitimately empty inbox, which stays an honest
// "no requests yet." Keeps the panel from ever looking broken if the API
// hiccups, without ever fabricating real-looking leads.
const now = new Date().toISOString();
const MOCK_REQUESTS = [
  { id: "mock-1", name: "Jordan Reyes", email: "jordan.reyes@example.com", phone: "(415) 555-0148", message: "Interested in scheduling a tour this weekend and want to talk through financing options.", listing: { address: "1018 Noe St, San Francisco, CA" }, status: "new", created_at: now, is_mock: true },
  { id: "mock-2", name: "Priya Nair", email: "priya.nair@example.com", phone: null, message: "Can someone walk me through the offer process before I submit?", listing: null, status: "contacted", created_at: now, is_mock: true },
];

// Broker-side inbox for "talk to a human" requests raised from Ask Cisero in
// the buyer flow (broker_requests table) — previously these only ever
// reached an ops email inbox with no in-app surface at all. Reads via a
// service-role backend action (RLS on broker_requests is buyer-owner-only,
// a broker can't read the table directly with the anon client) and lets a
// broker triage: mark contacted/closed, or hand off into a real managed
// client via onConvertToClient.
export default function BrokerRequestsPanel({ onConvertToClient }) {
  const [requests, setRequests] = useState(null);
  const [loadFailed, setLoadFailed] = useState(false);
  const [filter, setFilter] = useState("open"); // "open" | "all"
  const [busyId, setBusyId] = useState(null);

  const load = () => {
    setLoadFailed(false);
    fetch("/api/listings?broker_requests=1", { headers: { "x-beta-token": BETA_SECRET } })
      .then((r) => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json(); })
      .then((d) => setRequests(d.requests || []))
      .catch(() => { setLoadFailed(true); setRequests(MOCK_REQUESTS); });
  };

  useEffect(() => { load(); }, []);

  const setStatus = async (id, status) => {
    setBusyId(id);
    if (!id.startsWith("mock-")) {
      await fetch("/api/listings", {
        method: "POST",
        headers: { "Content-Type": "application/json", "x-beta-token": BETA_SECRET },
        body: JSON.stringify({ action: "update_broker_request_status", id, status }),
      }).catch(() => {});
      track("broker_request_status_changed", { id, status });
    }
    setRequests((prev) => prev.map((r) => (r.id === id ? { ...r, status } : r)));
    setBusyId(null);
  };

  const visible = (requests || []).filter((r) => filter === "all" || r.status !== "closed");
  const newCount = (requests || []).filter((r) => r.status === "new").length;

  return (
    <div style={{ flex: 1, overflowY: "auto", padding: "20px 24px", maxWidth: 780, margin: "0 auto", width: "100%" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 18 }}>
        <div>
          <div style={{ fontFamily: "'Playfair Display',serif", fontSize: 22, fontWeight: 700, color: "#152d21" }}>Buyer requests</div>
          <div style={{ fontSize: 12, color: "#7aad98", marginTop: 2 }}>
            {newCount > 0 ? `${newCount} new · raised via Ask Cisero in the buyer flow` : "Raised via Ask Cisero in the buyer flow"}
          </div>
        </div>
        <div style={{ display: "flex", background: "#f2f8f5", borderRadius: 8, padding: 3, border: "1px solid #d8ede6" }}>
          {[["open", "Open"], ["all", "All"]].map(([v, label]) => (
            <button key={v} onClick={() => setFilter(v)} style={{
              background: filter === v ? "#fff" : "none", border: "none", borderRadius: 6,
              padding: "5px 12px", fontSize: 11, fontFamily: mono, letterSpacing: "0.06em",
              color: filter === v ? "#1c2b25" : "#9bbfb2", cursor: "pointer", fontWeight: filter === v ? 700 : 400,
            }}>{label}</button>
          ))}
        </div>
      </div>

      {loadFailed && (
        <div style={{ background: "#fef4e8", border: "1px solid #f1d6a8", borderRadius: 10, padding: "8px 14px", marginBottom: 14, fontSize: 11, fontFamily: mono, color: "#c47c1a", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10 }}>
          <span>★ Couldn't load live requests — showing sample data.</span>
          <button onClick={load} style={{ background: "none", border: "1px solid #f1d6a8", color: "#c47c1a", borderRadius: 6, padding: "3px 10px", fontSize: 10, cursor: "pointer", fontFamily: mono }}>Retry</button>
        </div>
      )}

      {requests === null ? (
        <div style={{ textAlign: "center", padding: 40, color: "#9bbfb2" }}>Loading…</div>
      ) : visible.length === 0 ? (
        <div style={{ textAlign: "center", padding: 40, color: "#9bbfb2" }}>
          {filter === "open" ? "No open requests." : "No requests yet."}
        </div>
      ) : (
        visible.map((r) => (
          <div key={r.id} style={{ background: "#fff", border: "1px solid #e1ede7", borderRadius: 14, padding: "16px 18px", marginBottom: 12 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12, marginBottom: 8, flexWrap: "wrap" }}>
              <div>
                <div style={{ fontSize: 15, fontWeight: 700, color: "#152d21", fontFamily: sans }}>{r.name}</div>
                <div style={{ fontSize: 11, color: "#7aad98", fontFamily: mono, marginTop: 2 }}>
                  {r.email}{r.phone ? ` · ${r.phone}` : ""} · {fmtDate(r.created_at)}
                </div>
                {r.listing?.address && <div style={{ fontSize: 11, color: "#7aad98", fontFamily: mono, marginTop: 2 }}>Re: {r.listing.address}</div>}
              </div>
              <StatusBadge status={r.status} />
            </div>
            {r.message && (
              <div style={{ fontSize: 13, color: "#3d4f45", fontStyle: "italic", background: "#f7faf8", border: "1px solid #e1ede7", borderRadius: 10, padding: "10px 12px", marginBottom: 10 }}>
                "{r.message}"
              </div>
            )}
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              {r.status === "new" && (
                <button disabled={busyId === r.id} onClick={() => setStatus(r.id, "contacted")} style={{ background: "#2d7a62", color: "#fff", border: "none", borderRadius: 8, padding: "6px 14px", fontSize: 11, fontWeight: 700, cursor: "pointer", fontFamily: sans }}>
                  Mark contacted
                </button>
              )}
              {r.status !== "closed" && (
                <button disabled={busyId === r.id} onClick={() => setStatus(r.id, "closed")} style={{ background: "none", border: "1px solid #d8ede6", color: "#5b7365", borderRadius: 8, padding: "6px 14px", fontSize: 11, cursor: "pointer", fontFamily: sans }}>
                  Close
                </button>
              )}
              {onConvertToClient && (
                <button onClick={() => onConvertToClient(r)} style={{ background: "none", border: "1px solid #d8ede6", color: "#3b6ea8", borderRadius: 8, padding: "6px 14px", fontSize: 11, cursor: "pointer", fontFamily: sans }}>
                  + Add as client
                </button>
              )}
              <a href={`mailto:${r.email}`} style={{ background: "none", border: "1px solid #d8ede6", color: "#5b7365", borderRadius: 8, padding: "6px 14px", fontSize: 11, cursor: "pointer", fontFamily: sans, textDecoration: "none" }}>
                Email
              </a>
            </div>
          </div>
        ))
      )}
    </div>
  );
}
